// src/admin/pages/EquityAdminPage.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Users, 
  Briefcase, 
  FileText,
  Building
} from 'lucide-react';
import MasterAdminHeader from '../components/MasterAdminHeader';
import MasterAdminSidebar from '../components/MasterAdminSidebar';
import ShareholdersManagementPage from './ShareholdersManagementPage';
import CompaniesManagementPage from './CompaniesManagementPage';
import { useGlobalStore } from '../../state/globalStore';
import DirectorsListTab from './tabs/DirectorsListTab';
import IssuancesListTab from './tabs/IssuancesListTab';

const EquityAdminPage = () => {
  const { user, userProfile } = useGlobalStore();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get active tab from URL or default to companies
  const getActiveTabFromPath = () => {
    const path = location.pathname.replace('/adminhq/equity', '').replace(/^\//, '');
    return path || 'companies';
  };
  
  const [activeTab, setActiveTab] = useState(() => getActiveTabFromPath());

  // Equity-specific navigation items
  const equityNavItems = [
    { id: 'companies', name: 'Companies', icon: Building, color: 'blue' },
    { id: 'shareholders', name: 'Shareholders', icon: Users, color: 'green' },
    { id: 'directors', name: 'Directors', icon: Briefcase, color: 'purple' },
    { id: 'issuances', name: 'Share Issuances', icon: FileText, color: 'orange' },
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/adminhq/equity/${tabId}`);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'companies':
        return <CompaniesManagementPage />;
      case 'shareholders':
        return <ShareholdersManagementPage />;
      case 'directors':
        return <DirectorsListTab />;
      case 'issuances':
        return <IssuancesListTab />;
      default:
        return <CompaniesManagementPage />;
    }
  };

  // Redirect to first tab if no tab is specified
  useEffect(() => {
    const path = location.pathname.replace('/adminhq/equity', '').replace(/^\//, '');
    if (!path || path === '') {
      navigate('/adminhq/equity/companies', { replace: true });
      return;
    }
    // Update active tab when path changes
    setActiveTab(path);
  }, [location.pathname, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <MasterAdminSidebar activeTab="equity" setActiveTab={(tab) => navigate(`/adminhq/${tab}`)} />
      <div className="flex-1 flex flex-col">
        <MasterAdminHeader 
          user={user} 
          userProfile={userProfile} 
          title="Equity Manager Admin"
        />
        
        {/* Tabs Bar */}
        <div className="bg-white border-b border-gray-200 px-6">
          <div className="flex space-x-1">
            {equityNavItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleTabChange(item.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === item.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <item.icon className="h-4 w-4 mr-2" />
                  {item.name}
                </div>
              </button>
            ))}
          </div>
        </div>

        <main className="flex-1 overflow-auto">
          <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default EquityAdminPage;
